import styles from './client-sharing-settigns.module.scss';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ControlSwitch from './ControlSwitch';
import { useSelector, useDispatch } from 'react-redux';
import { updateExternalConfig } from 'api/externalAccess';
import actions from 'store/actions';
import { tools } from 'constants/externalAccess';
import { TextField, Button } from '@mui/material';
import { generateRandomPw } from 'utils/crypto';
import { ReactComponent as SearchIcon } from 'icons/search.svg';
import { ReactComponent as ShareLink } from 'icons/share-link.svg';
import { ReactComponent as AlertIcon } from 'icons/alert-triangle.svg';
import Snackbar from '@mui/material/Snackbar';
import { getUrl, openUrlInNewTab } from 'utils/paths';
import searchParams from 'constants/searchParams';
import paths from 'constants/paths';

export default function ClientSharingSettings() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const externalAccessConfig = useSelector((state) => state.externalAccessConfig);
  const [password, setPassword] = useState(
    externalAccessConfig.password ? externalAccessConfig.password : '',
  );
  const [showSnackbar, setShowSnackbar] = useState(false);
  const shareableLink = getUrl(
    `${paths.externalAccessLogin}?${searchParams.accessKey}=${externalAccessConfig.accessKey}`,
  );
  const [link, setLink] = useState(shareableLink);
  const [error, setError] = useState(false);

  const initLink = (value) => {
    if (value) {
      setError(false);
      setLink(shareableLink);
    }
  };

  const onSwitchChange = async (e, value) => {
    switch (e.target.name) {
      case tools.readOnlyTool:
        value && initLink(value);
        dispatch(
          actions.externalAccess.setExternalAccessConfig({
            ...externalAccessConfig,
            readModeEnabled: value,
          }),
        );
        await updateExternalConfig({ ...externalAccessConfig, readModeEnabled: value });
        break;

      case tools.interactiveTool:
        value && initLink(value);
        dispatch(
          actions.externalAccess.setExternalAccessConfig({
            ...externalAccessConfig,
            interactiveModeEnabled: value,
          }),
        );
        await updateExternalConfig({ ...externalAccessConfig, interactiveModeEnabled: value });
        break;

      case tools.pwTool:
        if (value && (!password || !password.length)) {
          const randomPassword = generateRandomPw();
          setPassword(randomPassword);
          dispatch(
            actions.externalAccess.setExternalAccessConfig({
              ...externalAccessConfig,
              passwordEnabled: value,
              password: randomPassword,
            }),
          );
          await updateExternalConfig({
            ...externalAccessConfig,
            passwordEnabled: value,
            password: randomPassword,
          });
        } else {
          dispatch(
            actions.externalAccess.setExternalAccessConfig({
              ...externalAccessConfig,
              passwordEnabled: value,
            }),
          );
          await updateExternalConfig({
            ...externalAccessConfig,
            passwordEnabled: value,
            password: password.length ? externalAccessConfig.password : '',
          });
        }

        break;

      default:
        return;
    }
  };

  const updatePassword = async () => {
    dispatch(
      actions.externalAccess.setExternalAccessConfig({
        ...externalAccessConfig,
        password,
      }),
    );
    await updateExternalConfig({ ...externalAccessConfig, password });
  };

  const showError = () => {
    setError(true);
    setLink('');
  };

  const onPreview = () => {
    if (!externalAccessConfig.readModeEnabled && !externalAccessConfig.interactiveModeEnabled) {
      showError();
      return;
    }
    openUrlInNewTab(`${shareableLink}&${searchParams.preview}=true`);
  };

  const onCopyLink = () => {
    if (!externalAccessConfig.readModeEnabled && !externalAccessConfig.interactiveModeEnabled) {
      showError();
      return;
    }
    navigator.clipboard.writeText(shareableLink);
    setShowSnackbar(true);
    setTimeout(() => {
      setShowSnackbar(false);
    }, 2000);
  };

  const passwordField = (
    <div className={styles.cc_input_field}>
      <TextField
        inputProps={{ 'data-testid': 'UpdatePasswordInput' }}
        className={styles.cc__input}
        disabled={!externalAccessConfig.passwordEnabled}
        onChange={(event) => setPassword(event.target.value)}
        value={password}
      />
      <Button
        data-testid="UpdatePasswordButton"
        disabled={
          password === externalAccessConfig.password ||
          !externalAccessConfig.passwordEnabled ||
          !password.length
        }
        className={styles.cc_assessment_link}
        variant="contained"
        onClick={updatePassword}>
        {t('clientConfig.updatePassword')}
      </Button>
    </div>
  );

  return (
    <div className={styles.settings__container}>
      <h2 className={styles.settings__title}>{t('clientConfig.clientSharingSettings')}</h2>
      <p className={styles.settings__description}>
        {t('clientConfig.clientSharingSettingsDescription')}
      </p>
      <div className={styles.settings__switchers_container}>
        <ControlSwitch
          title={t('clientConfig.readOnlyToolTitle')}
          description={t('clientConfig.readOnlyToolDescription')}
          onChange={onSwitchChange}
          name={tools.readOnlyTool}
          checked={externalAccessConfig.readModeEnabled}
          error={error}
        />
        <ControlSwitch
          title={t('clientConfig.interactiveToolTitle')}
          description={t('clientConfig.interactiveToolDescription')}
          onChange={onSwitchChange}
          name={tools.interactiveTool}
          checked={externalAccessConfig.interactiveModeEnabled}
          error={error}
        />
      </div>
      <ControlSwitch
        title={t('clientConfig.passwordToolTitle')}
        description={t('clientConfig.passwordToolDescription')}
        onChange={onSwitchChange}
        name={tools.pwTool}
        checked={externalAccessConfig.passwordEnabled}
        children={passwordField}
      />
      <div className={styles.cc__link_container}>
        <TextField
          inputProps={{ 'data-testid': 'ShareableLink' }}
          className={styles.cc__input}
          disabled
          value={link}
        />
        <Button
          startIcon={<SearchIcon />}
          data-testid="PreviewButton"
          className={styles.cc__link_button}
          variant="contained"
          onClick={onPreview}>
          {t('clientConfig.preview')}
        </Button>
        <Button
          startIcon={<ShareLink />}
          data-testid="CopyLinkButton"
          variant="contained"
          onClick={onCopyLink}>
          {t('clientConfig.copyLink')}
        </Button>
      </div>
      {error ? (
        <div data-testid="ErrorMessage" className={styles.cc__error_text}>
          <AlertIcon />
          <span>{t('clientConfig.error')}</span>
        </div>
      ) : null}
      <Snackbar
        data-testid="CopyLinkSnackbar"
        className={styles.cc__snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showSnackbar}
        message={t('clientConfig.snackbar')}
      />
    </div>
  );
}
